/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { oneOfType, bool, shape, object, string } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'clsx';

import { createUrl } from 'utils/urlHelper';
import { getWebPImage } from 'utils/image';

import MapProps from 'components/MapProps/MapProps';
import LikeButton from 'components/LikeButton/LikeButton';
import ManageButton from 'components/ManageButton/ManageButton';
import RequestButton from 'components/RequestButton/RequestButton';
import ChatButton from 'components/ChatButton/ChatButton';

import RoleData from './RoleData/RoleData';

import styles from './profile-header.scss';

/**
 * This is the ProfileHeader component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class ProfileHeader extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    bootstrap: shape({
      constants: object.isRequired,
    }).isRequired,
    history: object.isRequired,
    talent_id: string,
    fullname: string,
    title: string,
    location_city: string,
    location_country: string,
    slug: string,
    role_data: object,
    jobId: string,
    discussion_id: string,
    profile_picture: oneOfType([object, bool]),
    show_chat_button: bool,
    show_like_button: bool,
    show_manage_button: bool,
    show_request_button: bool,
    show_unlike_button: bool,
    compact: bool,
  };

  static defaultProps = {
    talent_id: '',
    fullname: '',
    title: '',
    location_city: '',
    location_country: '',
    slug: '',
    role_data: {},
    jobId: null,
    discussion_id: null,
    profile_picture: false,
    show_chat_button: false,
    show_like_button: false,
    show_manage_button: false,
    show_request_button: false,
    show_unlike_button: false,
    compact: false,
  };

  renderLocation = () => {
    const {
      location_city,
      location_country,
      bootstrap: { constants },
    } = this.props;

    return (
      <>
        {location_city},{' '}
        <MapProps value={location_country} list={constants.address_countries} />
      </>
    );
  };

  renderOptions = () => {
    const {
      talent_id,
      slug,
      jobId,
      discussion_id,
      show_chat_button,
      show_like_button,
      show_manage_button,
      show_request_button,
      show_unlike_button,
    } = this.props;

    return (
      <>
        {show_request_button && (
          <div className={styles.button}>
            <RequestButton
              wide
              small
              talentId={talent_id}
              jobId={jobId}
              slug={slug}
            />
          </div>
        )}

        {(show_like_button || show_unlike_button) && (
          <div className={styles.button}>
            <LikeButton
              white
              ghost
              wide
              small
              slug={slug}
              talentId={talent_id}
              jobId={jobId}
              callback={this.onClickLike}
              active={show_unlike_button}
            />
          </div>
        )}

        {show_chat_button && (
          <div className={styles.button}>
            <ChatButton
              wide
              small
              jobId={jobId}
              discussionId={discussion_id}
              slug={slug}
            />
          </div>
        )}

        {show_manage_button && (
          <div className={styles.button}>
            <ManageButton 
              white
              ghost
              wide
              small
              talentId={talent_id} />
          </div>
        )}
      </>
    );
  };

  onClickLike = payload => {
    const { history } = this.props;
    const { job_id } = payload;

    history.push(createUrl('job', job_id));
  };

  /**
   * Render ProfileHeader.
   *
   * @return {Object}
   */
  render() {
    const { fullname, title, profile_picture, role_data, compact } = this.props;

    return (
      <header
        className={classNames(styles.wrapper, {
          [styles.compact]: compact,
        })}
      >
        <div className={styles.headerContent}>
          <div className={styles.headerRow}>
            <div
              className={classNames(styles.headerText, {
                [styles.compact]: compact,
              })}
            >
              <h1 className={styles.title}>{fullname}</h1>
              <div className={styles.strapline}></div>
              <p className={styles.info}>
                {title && `${title},`} {this.renderLocation()}
              </p>

              <div className={styles.roleData}>
                <RoleData data={role_data} />
              </div>

              <div className={styles.options}>{this.renderOptions()}</div>
            </div>
          </div>
        </div>
        <div
          className={classNames(styles.image, {
            [styles.compact]: compact,
          })}
          style={
            profile_picture
              ? {
                  backgroundImage: `url(${getWebPImage(
                    profile_picture.landscape_face_url
                  )})`,
                }
              : {}
          }
        />
      </header>
    );
  }
}

export default withRouter(
  connect(({ bootstrap }) => ({ bootstrap }))(ProfileHeader)
);
